import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { COVERAGE, GRID_FONT, GRID_RADIUS, INK } from "../../../../theme/tokens";
import { leadingOffsets } from "../gridGeometry";
import { CoverageLocationEditor } from "../LocationConfigEditor/CoverageLocationEditor";
import { coverageRowLabel, type CoverageRow as RowType } from "../types";
import { useCoverageGridContext } from "./CoverageGridContext";
import { CoverageIdentityLabel } from "./CoverageIdentityLabel";
import { StickyLeadingCell } from "./StickyLeadingCell";

const { shift: SHIFT_LEFT, role: ROLE_LEFT } = leadingOffsets("standard");

type Props = {
	row: RowType;
	isExpanded: boolean;
	isEditorOpen: boolean;
	/** Stable toggle for this row's in-place detail (names). */
	onToggleExpanded: (rowId: string) => void;
	/** Stable open/close for this row's location-config editor. */
	onEditorOpenChange: (rowId: string, open: boolean) => void;
};

/** The chevron that toggles a row's in-place detail — sits before the location, always visible. */
const ExpandChevron = ({ row, isExpanded, rowLabel, onToggle }: { row: RowType; isExpanded: boolean; rowLabel: string; onToggle: () => void }) => {
	const { t } = useTranslation();
	return (
		<Box
			component="button"
			type="button"
			data-testid={`ms3-row-expand-${row.id}`}
			aria-label={t(isExpanded ? "schedule.template.v3.row.collapseAria" : "schedule.template.v3.row.expandAria", { row: rowLabel })}
			aria-expanded={isExpanded}
			onClick={(e) => {
				e.stopPropagation();
				onToggle();
			}}
			sx={(theme) => ({
				flexShrink: 0,
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: 18,
				height: 18,
				p: 0,
				border: "none",
				borderRadius: GRID_RADIUS.BADGE,
				backgroundColor: "transparent",
				color: INK.MUTED,
				cursor: "pointer",
				"@media (hover: hover)": { "&:hover": { backgroundColor: theme.palette.action.hover, color: INK.SECONDARY } },
				"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "-2px" },
			})}
		>
			<KeyboardArrowRightIcon sx={{ fontSize: 16, transition: "transform 150ms", transform: isExpanded ? "rotate(90deg)" : "none" }} />
		</Box>
	);
};

/**
 * The three sticky leading (identity) cells of a Coverage · Summary row —
 * Location (expand chevron + editable/read-only name), Shift, and Role + rule
 * chip. Split out of `CoverageRow` so the identity block and the day-cell loop
 * are each small and independently memoizable. Reads grid-wide `editWiring` from
 * context rather than props.
 */
export const CoverageRowIdentity = memo(({ row, isExpanded, isEditorOpen, onToggleExpanded, onEditorOpenChange }: Props) => {
	const { t } = useTranslation();
	const { editWiring } = useCoverageGridContext();
	// Job-managed depts key each row by job and send its display name; render that verbatim.
	// Non-job depts send no `jobName`, so fall back to the role-set label.
	const identityLabel = coverageRowLabel(row, t);
	const rowLabel = `${row.location} · ${identityLabel} · ${row.shift}`;
	// The Location cell is editable only when wiring is present, editing isn't
	// blocked, and the row isn't an outside-config (unbacked) row.
	const isEditable = !!editWiring && !editWiring.editBlocked && !row.outsideConfig;

	return (
		<>
			<StickyLeadingCell left={0} variant="body" role="rowheader" emphasis="primary" gap={0.5}>
				<ExpandChevron row={row} isExpanded={isExpanded} rowLabel={rowLabel} onToggle={() => onToggleExpanded(row.id)} />
				{isEditable && editWiring ? (
					<CoverageLocationEditor
						row={row}
						isOpen={isEditorOpen}
						onOpenChange={(open) => onEditorOpenChange(row.id, open)}
						weekStartDay={editWiring.weekStartDay}
						uniqueSections={editWiring.uniqueSections}
						onCoverageRefresh={editWiring.onCoverageRefresh}
					>
						<InHouseTooltip title={row.location}>
							<Box
								component="button"
								type="button"
								data-testid={`ms3-location-cell-${row.id}`}
								aria-label={t("schedule.template.v3.editor.openAria", { location: row.location })}
								onClick={(e) => {
									e.stopPropagation();
									onEditorOpenChange(row.id, !isEditorOpen);
								}}
								sx={(theme) => ({
									flex: 1,
									minWidth: 0,
									alignSelf: "stretch",
									display: "flex",
									alignItems: "center",
									gap: 0.5,
									p: 0,
									border: "none",
									background: "transparent",
									font: "inherit",
									color: "inherit",
									textAlign: "left",
									cursor: "pointer",
									"& .ms3-loc-edit-icon": { opacity: 0, transition: "opacity 150ms" },
									"@media (hover: hover)": {
										"&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
										"&:hover .ms3-loc-edit-icon": { opacity: 1 },
									},
									"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "-2px" },
								})}
							>
								<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{row.location}
								</Box>
								<EditIcon className="ms3-loc-edit-icon" sx={{ fontSize: 13, color: INK.MUTED, flexShrink: 0, ml: "auto" }} />
							</Box>
						</InHouseTooltip>
					</CoverageLocationEditor>
				) : (
					<InHouseTooltip title={editWiring?.editBlocked ? editWiring.editBlockedTooltip : row.location}>
						<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{row.location}
						</Box>
					</InHouseTooltip>
				)}
			</StickyLeadingCell>
			<StickyLeadingCell left={SHIFT_LEFT} variant="body" role="rowheader" emphasis="secondary" gap={0.5}>
				<span>{row.shift}</span>
				{row.outsideConfig && (
					<InHouseTooltip title={t("schedule.template.v3.outsideConfig")}>
						<Box component="span" sx={{ color: COVERAGE.WARNING_INK, fontWeight: 800, ml: 0.25 }}>
							*
						</Box>
					</InHouseTooltip>
				)}
			</StickyLeadingCell>
			<StickyLeadingCell left={ROLE_LEFT} variant="body" role="rowheader" emphasis="secondary" showEdgeShadow gap={0.5}>
				<CoverageIdentityLabel
					label={identityLabel}
					unconfiguredRoles={row.unconfiguredRoles}
					baseColor={INK.PRIMARY}
					sx={{ fontSize: GRID_FONT.SM, flexShrink: 0 }}
				/>
			</StickyLeadingCell>
		</>
	);
});
